// Overlap group lifecycle control — status pills + encrypted note editor.
// Story #5305 Stream 14b (Task #5318), design §6.3, replacing GroupDetail.
// tsx's own read-only placeholder ("Status: ... (read-only — Stream 14b owns
// changing it)").
//
// Two independent role floors, both enforced server-side (this component
// only mirrors them so a below-floor click never round-trips for nothing):
//   - Reviewing / Consolidating: any active team member entitled to every
//     project in the group.
//   - Consolidated / Dismissed: admin or billing owner only (design §6.3:
//     "dismissing or marking consolidated requires an admin or the billing
//     owner").
// The note editor has NO extra floor beyond aggregator membership (design:
// "any active team member can... add the note") — it fails closed instead,
// with plain copy, when the aggregator has no usable organisation key
// (design §6.4's "notes... are simply unavailable, fail-closed").
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Lock } from 'lucide-react'
import {
  getOverlapGroupNote,
  putOverlapGroupNote,
  setOverlapGroupStatus,
} from '../../services/api'
import { OVERLAP_STALE_GROUP_MESSAGE } from '../../lib/whitespace'
import type { AggregatorRole } from '../../types'
import type { OverlapGroupLifecycleStatus, OverlapGroupStatusTarget } from '../../types/whitespace'

const MAX_NOTE_CHARS = 2000

const STATUS_TARGETS: { value: OverlapGroupStatusTarget; label: string }[] = [
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'consolidating', label: 'Consolidating' },
  { value: 'consolidated', label: 'Consolidated' },
  { value: 'dismissed', label: 'Dismiss' },
]

const HIGH_FLOOR_STATUSES: OverlapGroupStatusTarget[] = ['consolidated', 'dismissed']
const HIGH_FLOOR_ROLES: AggregatorRole[] = ['admin', 'billing_owner']

interface OverlapLifecycleControlProps {
  groupId: string
  aggregatorId: string | null
  status: OverlapGroupLifecycleStatus | null
  /** null while the caller's role hasn't loaded yet — every pill renders
   * disabled until it has, so a status change is never attempted against a
   * floor this component hasn't actually checked. */
  callerRole: AggregatorRole | null
  onStatusChanged: (next: OverlapGroupStatusTarget) => void
}

export default function OverlapLifecycleControl({
  groupId, aggregatorId, status, callerRole, onStatusChanged,
}: OverlapLifecycleControlProps) {
  const qc = useQueryClient()
  const [noteOpen, setNoteOpen] = useState(false)
  const [noteDraft, setNoteDraft] = useState('')
  const [notice, setNotice] = useState<string | null>(null)

  const noteQuery = useQuery({
    queryKey: ['overlap-group-note', aggregatorId, groupId],
    queryFn: () => getOverlapGroupNote(groupId, aggregatorId),
    enabled: noteOpen,
  })

  useEffect(() => {
    if (noteQuery.data?.available) setNoteDraft(noteQuery.data.note ?? '')
  }, [noteQuery.data])

  const statusMutation = useMutation({
    mutationFn: (target: OverlapGroupStatusTarget) => setOverlapGroupStatus(groupId, target, aggregatorId),
    onSuccess: (result) => {
      setNotice(null)
      onStatusChanged(result.status)
      qc.invalidateQueries({ queryKey: ['portfolio-overlap-group', aggregatorId, groupId] })
      qc.invalidateQueries({ queryKey: ['portfolio-overlap', aggregatorId] })
    },
    onError: (e) => {
      const status = (e as { response?: { status?: number } })?.response?.status
      setNotice(status === 404 ? OVERLAP_STALE_GROUP_MESSAGE
        : status === 403 ? 'Only Admin or Billing Owner can set this status.'
        : (e as Error)?.message ?? 'Failed to update status.')
    },
  })

  const noteMutation = useMutation({
    mutationFn: (note: string) => putOverlapGroupNote(groupId, note, aggregatorId),
    onSuccess: (result) => {
      qc.setQueryData(['overlap-group-note', aggregatorId, groupId], result)
      setNotice(result.available ? 'Note saved.' : null)
    },
    onError: (e) => {
      const status = (e as { response?: { status?: number } })?.response?.status
      setNotice(status === 404 ? OVERLAP_STALE_GROUP_MESSAGE : (e as Error)?.message ?? 'Failed to save the note.')
    },
  })

  const isHighFloorRole = callerRole != null && HIGH_FLOOR_ROLES.includes(callerRole)

  return (
    <div data-testid="overlap-lifecycle-control">
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="overlap-status-pills">
        <span className="text-[11px] text-gray-500">Status:</span>
        {STATUS_TARGETS.map((s) => {
          const active = status === s.value
          const needsHighFloor = HIGH_FLOOR_STATUSES.includes(s.value)
          const belowFloor = callerRole == null || (needsHighFloor && !isHighFloorRole)
          return (
            <button
              key={s.value}
              type="button"
              disabled={belowFloor || statusMutation.isPending}
              title={belowFloor && callerRole != null ? 'Only Admin or Billing Owner can set this status' : undefined}
              onClick={() => statusMutation.mutate(s.value)}
              data-testid={`overlap-status-pill-${s.value}`}
              aria-pressed={active}
              className={`text-[10.5px] font-bold rounded-full px-2.5 py-1 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                active
                  ? 'bg-iflow-900 text-white border-iflow-900'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-iflow-400'
              }`}
            >
              {s.label}
            </button>
          )
        })}
        {statusMutation.isPending && <Loader2 className="w-3 h-3 animate-spin text-iflow-500" />}
        <button
          type="button"
          onClick={() => setNoteOpen((v) => !v)}
          className="text-[10.5px] text-iflow-700 hover:text-iflow-800 font-semibold hover:underline ml-1"
          data-testid="overlap-note-toggle"
        >
          {noteOpen ? 'Hide note' : 'Note'}
        </button>
      </div>

      {notice && (
        <p className="text-[10.5px] text-gray-600 mt-1" role="status" data-testid="overlap-lifecycle-notice">
          {notice}
        </p>
      )}

      {noteOpen && (
        <div className="mt-2 p-2.5 bg-sky-500/5 rounded-lg" data-testid="overlap-note-editor">
          {noteQuery.isLoading ? (
            <p className="text-[11px] text-gray-500">Loading note…</p>
          ) : noteQuery.data && !noteQuery.data.available ? (
            <div className="flex items-start gap-1.5 text-[11px] text-gray-500" data-testid="overlap-note-unavailable">
              <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{noteQuery.data.copy}</span>
            </div>
          ) : (
            <>
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value.slice(0, MAX_NOTE_CHARS))}
                maxLength={MAX_NOTE_CHARS}
                rows={3}
                placeholder="Add a note for your team — encrypted at rest under your organisation's key."
                className="w-full text-[11.5px] border border-sky-200 rounded-lg px-2 py-1.5"
                data-testid="overlap-note-textarea"
              />
              <div className="flex items-center justify-between mt-1">
                <span className="text-[10px] text-gray-400">{noteDraft.length}/{MAX_NOTE_CHARS}</span>
                <button
                  type="button"
                  onClick={() => noteMutation.mutate(noteDraft)}
                  disabled={noteMutation.isPending}
                  className="btn pri text-[10.5px] px-2.5 py-1 disabled:opacity-50"
                  data-testid="overlap-note-save"
                >
                  {noteMutation.isPending ? 'Saving…' : 'Save note'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
